import React, { useState } from "react";
import formatValue from "../../../utils/formatValue";
import DashboardEmptyState from "../components/DashboardEmptyState";

interface RevenueRow {
  date: string;
  final_value: number;
  cumulative_value: number;
}

interface RevenueTableListProps {
  bills: RevenueRow[];
}

const PAGE_SIZE = 7;

export default function RevenueTableList({ bills }: RevenueTableListProps) {
  const [page, setPage] = useState(1);

  const sortedBills = [...bills].sort((a, b) => {
    const [dayA, monthA, yearA] = a.date.split("/");
    const [dayB, monthB, yearB] = b.date.split("/");
    return (
      new Date(`${yearB}-${monthB}-${dayB}`).getTime() -
      new Date(`${yearA}-${monthA}-${dayA}`).getTime()
    );
  });

  const totalPages = Math.max(1, Math.ceil(sortedBills.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const displayableBills = sortedBills.slice(startIndex, startIndex + PAGE_SIZE);

  if (sortedBills.length === 0) {
    return (
      <DashboardEmptyState
        align="center"
        title="Nenhuma receita registrada"
        description="Os valores recebidos aparecerão aqui conforme os boletos forem pagos."
        className="w-full py-4"
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="hidden w-full grid-cols-[minmax(100px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)] gap-3 px-4 text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:grid">
        <span>Data</span>
        <span>Total recebido</span>
        <span>Total acumulado</span>
      </div>

      {displayableBills.map((bill) => (
        <div
          key={`${bill.date}-${bill.final_value}`}
          className="w-full rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4 transition-colors hover:border-[#6ea8ff]/25 md:grid md:grid-cols-[minmax(100px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)] md:items-center md:gap-3 md:rounded-xl md:px-4 md:py-3"
        >
          <div className="mb-2 md:mb-0">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
              Data
            </p>
            <p className="text-sm font-semibold text-[#f0f6fc]">{bill.date}</p>
          </div>

          <div className="mb-2 md:mb-0">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
              Total recebido
            </p>
            <p className="text-sm font-bold text-emerald-400">{formatValue(bill.final_value)}</p>
          </div>

          <div>
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
              Total acumulado
            </p>
            <p className="text-sm font-bold text-[#6ea8ff]">{formatValue(bill.cumulative_value)}</p>
          </div>
        </div>
      ))}

      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-center gap-2">
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              onClick={() => setPage(pageNumber)}
              className={`min-w-[2rem] rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                page === pageNumber
                  ? "border border-[#6ea8ff]/35 bg-[#6ea8ff]/20 text-[#6ea8ff]"
                  : "bg-transparent text-[#8b949e] hover:bg-white/5"
              }`}
            >
              {pageNumber}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
